import React, { useEffect, useState } from 'react';
import {
    Chart as ChartJS,
    LinearScale,
    PointElement,
    LineElement,
    Tooltip,
    Legend,
    ChartDataset,
} from 'chart.js';
import { Scatter } from 'react-chartjs-2';
import { generateLatex, getExportFilename, downloadLatex } from './latexExport';
import logo from './logo.png';
import './App.css';

ChartJS.register(LinearScale, PointElement, LineElement, Tooltip, Legend);

export type TupleData = {
    x: number,
    y: number,
    basis: string,
    dimension: number,
}

function App() {
    const [sequenceName, setSequenceName] = useState<string>('');
    const [sequence, setSequence] = useState<string>('');
    const [datasets, setDatasets] = useState<ChartDataset<"scatter", TupleData[]>[]>([]);

    useEffect(() => console.log(datasets), [datasets]);

    function handleExportLatex() {
        if (datasets.length === 0) {
            alert('No datasets to export.');
            return;
        }
        const content = generateLatex(datasets);
        const filename = getExportFilename(datasets);
        downloadLatex(content, filename);
    }

    return <div
        style={{
            textAlign: 'center',
            padding: '0 25px',
        }}
    >
        <a href='/'>
            <img
                src={logo}
                alt='spectral sequence plotter'
                style={{
                    width: 200,
                    margin: '20px 0',
                }}
            />
        </a>
        <div
            style={{
                width: '100%',
                maxWidth: '1200px',
                margin: '0 auto',
            }}
        >
            <Scatter
                data={{ datasets: datasets }}
                options={{
                    plugins: {
                        tooltip: {
                            callbacks: {
                                afterLabel: ctx => {
                                    const data = datasets[ctx.datasetIndex].data;
                                    const currentPoint = data.find(list => list.x === ctx.parsed.x && list.y === ctx.parsed.y);
                                    return `* Dimension: ${currentPoint?.dimension}\n* Basis: ${currentPoint?.basis} `;
                                }
                            }
                        },
                    },
                    scales: {
                        x: {
                            ticks: {
                                stepSize: 1,
                            },
                            grid: {
                                color(ctx) {
                                    if (ctx.tick.value === 0) return '#000';
                                    else return '#E5E5E5';
                                },
                            },
                        },
                        y: {
                            ticks: {
                                stepSize: 1,
                            },
                            grid: {
                                color(ctx) {
                                    if (ctx.tick.value === 0) return '#000';
                                    else return '#E5E5E5';
                                },
                            }
                        },
                    },
                }}
            />

            <button type='button' onClick={handleExportLatex} disabled={datasets.length === 0}>
                Export to LaTeX
            </button>

            <form
                action='submit'
                onSubmit={formatSequence}
                style={{
                    display: 'flex',
                    flexFlow: 'column',
                    justifyContent: 'stretch',
                    width: '75%',
                    margin: '25px auto',
                }}
            >
                <input
                    type='text'
                    value={sequenceName}
                    onChange={e => setSequenceName(e.target.value)}
                    placeholder='Enter Spectral Sequence Name...'
                    style={{
                        padding: 15,
                        marginBottom: 15,
                    }}
                />
                <textarea
                    onChange={e => setSequence(e.target.value)}
                    value={sequence}
                    placeholder='Enter Spectral Sequence...'
                    style={{
                        minHeight: 250,
                        marginBottom: 15,
                        padding: 15,
                        resize: 'none',
                    }}
                />
                <button type='submit'>
                    Add Spectral Sequence
                </button>
            </form>
        </div>
    </div>;

    function formatSequence(e: React.FormEvent) {
        e.preventDefault();

        // Sequence without dashes
        const sequenceNoDashes = sequence.replaceAll(/--+/g, '');

        // Remove curly braces surrouding the sequence
        const innerSequence = sequenceNoDashes.slice(1, -1);

        // Break each x coordinate into a list
        const xCoordinates = innerSequence.split(')},');

        // Remove curly braces in front of the x coordinates and restore the end parenthesis
        const xCoordinatesNoBraces = xCoordinates.map(x => {
            let sliced = x.trim().substring(1);
            sliced += ')';
            return sliced;
        });

        // Map x coordinates into lists of entries
        const xCoordinateEntries: string[][] = [];
        xCoordinatesNoBraces.forEach(x => xCoordinateEntries.push(x.split('),')));

        // Remove parenthesis and spaces from the xCoordinateEntries
        const xCoordinateEntriesFormatted = xCoordinateEntries.map(x => x.map(i => i.replaceAll(/[()\s]/g, '')));


        // Map each x coordinate entry item into a TupleData object to be graphed
        const tupleData: TupleData[] = [];
        xCoordinateEntriesFormatted.forEach(entry => {
            entry.forEach(item => {
                // Split the string for each entry into an array of items
                const itemList = item.split(',');

                // First check for the dimension andif that is 0, do not add to the array of data to graph
                const dimension = parseInt(itemList[itemList.length - 1]);
                if (dimension === 0) {
                    return;
                }

                // Get the x coordinate from the first item in the list
                const x = parseInt(itemList[0]),
                    // Get the y coordinate from the second item in the list
                    y = parseInt(itemList[1]),
                    // Get the basis which is stored between two {} braces, remove the braces and add spaces between the commas
                    basisString = item.slice(item.indexOf('{'), item.indexOf('}')),
                    basisNoBraces = basisString.replaceAll(/[{}]/g, ''),
                    basis = basisNoBraces.replaceAll(',', ', ');

                // Add the data to graph
                tupleData.push({
                    x,
                    y,
                    basis,
                    dimension,
                });
            });
        });

        // Get a random color for the sequence points
        const colors = ['red', 'blue', 'green', 'black'];
        const colorIndex = Math.floor(Math.random() * colors.length);

        // Add the sequence to the scatter plot data, with the TupleData objects and color
        datasets.push({
            label: sequenceName,
            data: tupleData,
            backgroundColor: colors[colorIndex],
        } as ChartDataset<"scatter", TupleData[]>);

        setDatasets([...datasets]);
    }
}

export default App;
